import {
  useForm,
  useFieldArray,
  Controller,
  FieldValues,
  UseFormSetValue,
} from "react-hook-form";
import { FaRegCircleCheck } from "react-icons/fa6";
import { FiRefreshCw } from "react-icons/fi";
import { IoMdAdd, IoMdRemove } from "react-icons/io";
import Button from "components/button/Button";
import { Modal } from "components/modal";
import Input from "components/input/InputField";
import Checkbox from "components/input/Checkbox";
import { useTranslation } from "react-i18next";
import { formatDateTime, trimStrings } from "services/helper";
import { Field } from "components/dynamic/DynamicForm";
import { useEffect } from "react";

export type FieldChangeType = (
  value: string | number | null,
  setValue?: UseFormSetValue<FieldValues>
) => void;

interface FormModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (formData: FieldValues) => void | Promise<void>;
  fields: Field[];
  defaultValues?: FieldValues;
  title: string;
  description?: string;
  isEdit?: boolean;
  maxWidth?: string;
  loading?: boolean;
}

const FormModal = ({
  open,
  onClose,
  onSubmit,
  fields,
  defaultValues = {},
  title,
  description,
  isEdit,
  maxWidth = "md:max-w-[600px] lg:max-w-[750px]",
  loading,
}: FormModalProps) => {
  const { t } = useTranslation();

  const arrayField = fields.find((f) => f.type === "array");
  const otherFields = fields.filter((f) => f.type !== "array");

  const {
    handleSubmit,
    control,
    reset,
    formState: { dirtyFields },
  } = useForm({
    defaultValues: defaultValues || {},
  });

  const {
    fields: arrayItems,
    append,
    remove,
  } = useFieldArray({
    control,
    name: arrayField?.name || "items",
  });

  useEffect(() => {
    if (open) {
      const fixedDefaults = { ...defaultValues };

      otherFields.forEach((field) => {
        const val = fixedDefaults[field.name];
        if (val === undefined || val === null) {
          if (field.type === "date" || field.type === "time") {
            fixedDefaults[field.name] = null;
          } else if (field.type === "select" && field.multiple) {
            fixedDefaults[field.name] = [];
          } else {
            fixedDefaults[field.name] = "";
          }
        }
      });

      if (arrayField) {
        const arrayName = arrayField.name;
        if (
          !fixedDefaults[arrayName] ||
          fixedDefaults[arrayName].length === 0
        ) {
          fixedDefaults[arrayName] = [
            {
              name: "",
              index: "",
              factor: "",
              key: "",
              group: "",
              reverse: false,
            },
          ];
        }
      }

      reset(fixedDefaults);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, reset]);

  const handleAddItem = () => {
    if (arrayField) {
      const newItem: Record<string, any> = {};
      const arrayFields = (arrayField?.arrayFields as Field[]) || [];

      arrayFields.forEach((field) => {
        if (field.type === "number") {
          newItem[field.name] = 0;
        } else if (field.type === "checkbox") {
          newItem[field.name] = false;
        } else if (field.type === "select" && field.multiple) {
          newItem[field.name] = [];
        } else {
          newItem[field.name] = "";
        }
      });

      newItem.index = arrayItems.length + 1;
      newItem.factor = 1;
      newItem.reverse = false;
      newItem.key = "";

      append(newItem);
    }
  };

  const handleRemoveItem = (index: number) => {
    if (arrayItems.length > 1) {
      remove(index);
    }
  };

  const submitHandler = (data: FieldValues) => {
    const trimmedData = trimStrings(data);
    const formattedData = formatDateTime(trimmedData, fields);

    const result: Record<string, unknown> = isEdit
      ? (Object.keys(dirtyFields) as Array<keyof typeof formattedData>).reduce(
          (acc, key) => {
            acc[key] = formattedData[key];
            return acc;
          },
          {} as Record<string, unknown>
        )
      : formattedData;

    onSubmit(result);
  };

  const arrayFields = (arrayField?.arrayFields as Field[]) || [];

  return (
    <Modal
      isOpen={open}
      onClose={onClose}
      maxWidth={maxWidth}
      showCloseButton={true}
    >
      <div className="p-6">
        <form onSubmit={handleSubmit(submitHandler)} noValidate>
          <div className="mb-6 border-b border-gray-200 pb-4 dark:border-gray-700">
            <h2 className="text-xl font-bold text-gray-800 dark:text-white">
              {title}
            </h2>
            {description && (
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                {description}
              </p>
            )}
          </div>
          <div className="custom-scrollbar max-h-[65vh] space-y-4 overflow-y-auto p-4">
            <div className="mb-6 space-y-4">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                {otherFields.map((field) => (
                  <div
                    key={field.name}
                    className={`col-span-${field.gridCol || 1} ${
                      field.gridCol === 2 ? "md:col-span-2" : ""
                    }`}
                  >
                    <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
                      {field.label}{" "}
                      {field.required && (
                        <span className="text-red-500">*</span>
                      )}
                    </label>
                    <Controller
                      name={field.name}
                      control={control}
                      rules={{
                        required: field.required
                          ? `${field.label} ${t("validation.required")}`
                          : false,
                      }}
                      render={({
                        field: controllerField,
                        fieldState: { error },
                      }) => (
                        <Input
                          {...controllerField}
                          type={field.type === "number" ? "number" : "text"}
                          placeholder={field.label}
                          error={!!error}
                          hint={error?.message}
                          disabled={field.disabled}
                        />
                      )}
                    />
                  </div>
                ))}
              </div>
            </div>

            {arrayField && (
              <div className="mb-6">
                <div className="mb-4 flex items-center justify-between">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    {arrayField.label}{" "}
                    {arrayField.required && (
                      <span className="text-red-500">*</span>
                    )}
                  </label>
                  <Button
                    size="sm"
                    variant="info"
                    type="button"
                    onClick={handleAddItem}
                    startIcon={<IoMdAdd size={16} />}
                  >
                    {t("buttons.add")}
                  </Button>
                </div>

                <div className="space-y-4">
                  {arrayItems.map((item, index) => (
                    <div
                      key={item.id}
                      className="relative rounded-lg border border-gray-200 bg-gray-50/50 p-4 dark:border-gray-700 dark:bg-gray-800/50"
                    >
                      {arrayItems.length > 1 && (
                        <button
                          type="button"
                          onClick={() => handleRemoveItem(index)}
                          className="absolute end-2 top-2 rounded-lg bg-red-50 p-1.5 text-red-500 transition-colors hover:bg-red-100 dark:bg-red-900/20 dark:hover:bg-red-900/30"
                        >
                          <IoMdRemove className="size-4" />
                        </button>
                      )}

                      <div className="mb-3 text-xs font-medium text-gray-500 dark:text-gray-400">
                        {t("actions.item")} {index + 1}
                      </div>

                      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
                        {arrayFields.map((field) => {
                          if (field.type === "checkbox") {
                            return (
                              <div
                                key={field.name}
                                className={`col-span-${
                                  field.gridCol || 1
                                } flex flex-col items-center justify-center`}
                              >
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                  {field.label}
                                </label>
                                <Controller
                                  name={`${arrayField.name}.${index}.${field.name}`}
                                  control={control}
                                  render={({ field: controllerField }) => (
                                    <Checkbox
                                      checked={controllerField.value || false}
                                      onChange={controllerField.onChange}
                                      label={field.label}
                                      disabled={field.disabled}
                                    />
                                  )}
                                />
                              </div>
                            );
                          }

                          return (
                            <div
                              key={field.name}
                              className={`col-span-${field.gridCol || 1}`}
                            >
                              <label className="mb-1.5 block text-xs font-medium text-gray-700 dark:text-gray-300">
                                {field.label}{" "}
                                {field.required && (
                                  <span className="text-red-500">*</span>
                                )}
                              </label>
                              <Controller
                                name={`${arrayField.name}.${index}.${field.name}`}
                                control={control}
                                rules={{
                                  required: field.required
                                    ? `${field.label} ${t(
                                        "validation.required"
                                      )}`
                                    : false,
                                }}
                                render={({
                                  field: controllerField,
                                  fieldState: { error },
                                }) => (
                                  <Input
                                    {...controllerField}
                                    type={
                                      field.type === "number"
                                        ? "number"
                                        : "text"
                                    }
                                    placeholder={field.label}
                                    error={!!error}
                                    hint={error?.message}
                                    disabled={field.disabled}
                                  />
                                )}
                              />
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>

                <Button
                  size="sm"
                  variant="outline"
                  type="button"
                  onClick={handleAddItem}
                  startIcon={<IoMdAdd size={16} />}
                  className="mt-4 w-full border-dashed"
                >
                  {t("buttons.add")}
                </Button>
              </div>
            )}
          </div>

          <div className="flex items-center justify-between gap-3 border-t border-gray-200 pt-4 dark:border-gray-700">
            <Button
              variant="outline"
              size="sm"
              onClick={() => reset()}
              startIcon={<FiRefreshCw size={16} />}
            >
              {t("buttons.reset")}
            </Button>

            <div className="flex items-center gap-2">
              <Button variant="error" size="sm" onClick={onClose}>
                {t("buttons.cancel")}
              </Button>
              <Button
                type="submit"
                variant="primary"
                size="sm"
                loading={loading}
                startIcon={<FaRegCircleCheck size={16} />}
              >
                {t("buttons.submit")}
              </Button>
            </div>
          </div>
        </form>
      </div>
    </Modal>
  );
};

export default FormModal;
